import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import ReportDateRangeCsv from "../../../Common/ReportDateRangeCSV";
import { ProgressSpinner } from "primereact/progressspinner";

const DailyProductionReport = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const date = new Date();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const [inputs, setInputs] = useState({
    date_from: getCurrentDate(),
    date_to: getCurrentDate(),
  });
  const [loading, setLoading] = useState(false);
  function getCurrentDate() {
    const dateObj = new Date();
    const month = ("0" + (dateObj.getMonth() + 1)).slice(-2);
    const day = ("0" + dateObj.getDate()).slice(-2);
    const year = dateObj.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fetchData = (dateFromParam, dateToParam) => {
    const dateFrom = dateFromParam || inputs.date_from || getCurrentDate();
    const dateTo = dateToParam || inputs.date_to || getCurrentDate();
    setLoading(true);
    Http.get(
      `/production-and-transfer-list?date_from=${dateFrom}&date_to=${dateTo}`,
    )
      .then((res) => {
        setReceiveData(res.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const formSubmit = () => {
    fetchData(inputs.date_from, inputs.date_to);
  };
  const calculateRowValues = (item) => {
    const receivedFromBkl = (item.receive_data || []).reduce(
      (sum, r) => sum + Number(r.receive_qty || 0),
      0,
    );

    const biscuitLoss = (item.loss_data || []).reduce(
      (sum, l) => sum + Number(l.before_production_loss_qty || 0),
      0,
    );

    const glazeLoss = (item.loss_data || []).reduce(
      (sum, l) => sum + Number(l.after_production_loss_qty || 0),
      0,
    );

    const transferToGkl = (item.transfer_data || []).reduce(
      (sum, t) =>
        sum +
        (t.is_return === 0 || t.is_return === undefined
          ? Number(t.delivery_qty || 0)
          : 0),
      0,
    );

    const openingStock = 0;
    const totalWare = openingStock + receivedFromBkl;
    const totalLoss = biscuitLoss + glazeLoss;
    const totalWithLoss = totalWare + totalLoss;
    const closingBalance = totalWithLoss - transferToGkl;

    return {
      openingStock,
      receivedFromBkl,
      totalWare,
      transferToGkl,
      biscuitLoss,
      glazeLoss,
      totalLoss,
      totalWithLoss,
      closingBalance,
    };
  };

  const totals = receiveData.reduce(
    (acc, item) => {
      const {
        openingStock,
        receivedFromBkl,
        totalWare,
        transferToGkl,
        biscuitLoss,
        glazeLoss,
        totalLoss,
        totalWithLoss,
        closingBalance,
      } = calculateRowValues(item);

      return {
        totalOpeningStock: acc.totalOpeningStock + openingStock,
        totalReceivedFromBkl: acc.totalReceivedFromBkl + receivedFromBkl,
        totalTotalWare: acc.totalTotalWare + totalWare,
        totalTransferToGkl: acc.totalTransferToGkl + transferToGkl,
        totalBiscuitLoss: acc.totalBiscuitLoss + biscuitLoss,
        totalGlazeLoss: acc.totalGlazeLoss + glazeLoss,
        totalTotalLoss: acc.totalTotalLoss + totalLoss,
        totalTotalWithLoss: acc.totalTotalWithLoss + totalWithLoss,
        totalClosingBalance: acc.totalClosingBalance + closingBalance,
      };
    },
    {
      totalOpeningStock: 0,
      totalReceivedFromBkl: 0,
      totalTotalWare: 0,
      totalTransferToGkl: 0,
      totalBiscuitLoss: 0,
      totalGlazeLoss: 0,
      totalTotalLoss: 0,
      totalTotalWithLoss: 0,
      totalClosingBalance: 0,
    },
  );

  const getCsvData = () => {
    const headers = [
      "S/N",
      "Name of Biscuit Ware",
      "Shape",
      "Glaze",
      "Opening Stock",
      "Received From B.K.L",
      "Total Ware",
      "Trans. to G.K.L",
      "Biscuit Loss",
      "Glaze Loss",
      "Total Loss",
      "Total with Loss",
      "Closing Balance",
    ];

    const rows = receiveData.map((item, index) => {
      const {
        openingStock,
        receivedFromBkl,
        totalWare,
        transferToGkl,
        biscuitLoss,
        glazeLoss,
        totalLoss,
        totalWithLoss,
        closingBalance,
      } = calculateRowValues(item);
      return [
        index + 1,
        item.product_name,
        item.shape,
        item.glaze,
        openingStock,
        receivedFromBkl,
        totalWare,
        transferToGkl,
        biscuitLoss,
        glazeLoss,
        totalLoss,
        totalWithLoss,
        closingBalance,
      ];
    });

    const footerRows = [
      [
        "",
        "Total",
        "",
        "",
        totals.totalOpeningStock,
        totals.totalReceivedFromBkl,
        totals.totalTotalWare,
        totals.totalTransferToGkl,
        totals.totalBiscuitLoss,
        totals.totalGlazeLoss,
        totals.totalTotalLoss,
        totals.totalTotalWithLoss,
        totals.totalClosingBalance,
      ],
    ];

    return { headers, rows, footerRows };
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Daily Production Report</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-20 pd-sm-40">
              <ReportDateRangeCsv
                dateFrom={inputs.date_from}
                dateTo={inputs.date_to}
                onChange={handleChange}
                onSearch={formSubmit}
                csvFilename={`daily-production-report-glazing_${inputs.date_from}_to_${inputs.date_to}.csv`}
                getCsvData={getCsvData}
              />
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              {/* <h6 className="card-body-title mg-sm-b-30">Datatable</h6> */}
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <h5 className="text-uppercase text-sm font-weight-bolder">
                      Artisan Ceramics Limited
                    </h5>
                    <p>
                      House-16, Road-07, Sector-01, Uttara, Dhaka
                      <br />
                      <b>Daily Production Report</b>
                      <br />
                      (Glazing)
                    </p>
                  </div>
                  <div
                    className="table-responsive p-0 pb-2"
                    style={{ maxHeight: "70vh", overflowY: "auto" }}
                  >
                    <table className="table table-sm table-bordered table-striped align-items-center justify-content-center mb-0 _datatable">
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <tr>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            S/N
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            Name of Biscuit Ware
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Shape
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Glaze
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Opening Stock
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Received From B.K.L
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Total Ware
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Trans. to G.K.L
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Biscuit Loss
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Glaze Loss
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Total Loss
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Total with Loss
                          </th>
                          <th className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center">
                            Closing Balance
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td
                              className="text-center"
                              colSpan={13}
                              style={{ padding: "2rem" }}
                            >
                              <ProgressSpinner
                                style={{ width: "40px", height: "40px" }}
                                strokeWidth="4"
                              />
                            </td>
                          </tr>
                        ) : receiveData && receiveData.length > 0 ? (
                          receiveData.map((item, index) => {
                            const {
                              openingStock,
                              receivedFromBkl,
                              totalWare,
                              transferToGkl,
                              biscuitLoss,
                              glazeLoss,
                              totalLoss,
                              totalWithLoss,
                              closingBalance,
                            } = calculateRowValues(item);

                            return (
                              <tr key={index}>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {index + 1}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.product_name}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.shape}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {item.glaze}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {openingStock}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {receivedFromBkl}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {totalWare}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {transferToGkl}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {biscuitLoss}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {glazeLoss}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {totalLoss}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {totalWithLoss}
                                </td>
                                <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                                  {closingBalance}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              className="text-uppercase text-dark text-sm font-weight-bolder ps-2 text-center"
                              colSpan={13}
                            >
                              No data found
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td
                            className="text-uppercase text-dark text-sm font-weight-bolder ps-2"
                            colSpan={4}
                          >
                            Total
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalOpeningStock}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalReceivedFromBkl}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTotalWare}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTransferToGkl}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalBiscuitLoss}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalGlazeLoss}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTotalLoss}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalTotalWithLoss}
                          </td>
                          <td className="text-uppercase text-dark text-sm font-weight-bolder ps-2">
                            {totals.totalClosingBalance}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyProductionReport;
